import { NavList } from '@primer/react';
import { useAskAiEntryPoint } from '@upright/ui/chat/use-ask-ai-entry-point';
import { LinkWithSearch } from '@upright/ui/common/generic/components/navigation/link-with-search';
import {
  CUSTOM_COMPANIES_PATH,
  MANAGE_ACCESS_PATH,
  SCREEN_COMPANIES_PATH,
  SCREEN_FUNDS_PATH,
} from '@upright/ui/common/generic/components/navigation/page-paths';
import { ApplicationAdd, HomeChimney, OfficeWorkerStreamlineCore } from '@upright/ui/icons';
import BuildingOfficeIcon from '@upright/ui/icons/building-office-streamline-core';
import SearchDollarIcon from '@upright/ui/icons/search-dollar-streamline-core';
import ZoomDocumentIcon from '@upright/ui/icons/zoom-document-streamline-core';
import { showScreener, showSharedAssets, useFrontPageConfig } from '@upright/ui/views/home-views/front-page-config';
import { useOrganizationHasCreateCompaniesPermission } from '@upright/ui/views/home-views/custom-companies-view/hooks/use-organization-has-create-companies-permission';
import { OrganizationSelectorSidebar } from '@upright/ui/views/home-views/organizations-assets-view/organization-selector-sidebar';
import { useSelectedOrganization } from '@upright/ui/views/home-views/selected-organization-context';
import {
  getAriaCurrent,
  StyledNavList,
  TopLevelItemWrap,
} from '@upright/ui/views/shared/navigation/styled-nav-components';
import type { ComponentType, SVGProps } from 'react';
import { useLocation } from 'react-router-dom';
import { sharedAssetsLabel } from '@upright/ui/views/user-view/self-serve-labels';
import { useHasSelfServeAccess } from '@upright/ui/views/user-view/use-self-serve-access';
import { OrganizationRailRow } from './organization-rail-row';
import { ChatsRailSection, RailSection, railClassName, type RailRow } from './unified-investor-navigation';

/**
 * The unified shell's rail for a company front page (PLT-6943): the organisation selector, Home, the
 * Chats group, *My company*, then Database. The Chats group and the titled section are the
 * investor rail's own components, so the two rails differ only in which rows they carry.
 *
 * **What a company organisation's rail holds, and why.** The legacy frame gave company users the
 * investor rail, whose *Funds and portfolios* row led to their company dashboard. Here that
 * dashboard is *My company*, a row of its own. Database holds the screeners and Manage access (Shared assets for self-serve organisations), per
 * `showScreener` and `showSharedAssets` for a company client. There is no *My assets* group: a
 * company organisation holds no funds. No utility tail, because Favorites is not offered in this
 * shell (PLT-6697).
 *
 * **Custom companies only where the organisation can create them**, with no locked row for the
 * rest. The investor rail keeps its locked row as an upsell, but for company organisations the
 * capability is all but unused (one of them held the grant in September 2026), so a locked row on
 * every company rail would mostly advertise something that is not on offer. Everyone else can still reach the page by URL, where
 * it explains itself.
 *
 * It carries the investor rail's `railClassName` too, which sets every row label, top-level and
 * nested alike, to the rail's one size and lays the chat delete control over its row. Without it
 * the nested rows fall back to Primer's 12px sub-nav label beside 14px top-level rows.
 *
 * The order follows the investor rail, Home and then Chats on top and the destinations below
 * (Janne, PR #8847). *My company* is a single top-level row rather than a titled group of one row,
 * sitting where the investor rail's *My assets* group sits.
 *
 * Without a chat entry point there is no launcher, no Home row and no Chats group, and `/` renders
 * *My company*, so its row is current there too.
 */
export function UnifiedCompanyNavigation() {
  const { pathname } = useLocation();
  const { clientType } = useFrontPageConfig();
  const hasSelfServeAccess = useHasSelfServeAccess();
  // The gate the investor rail and every other chat entry point read; see
  // `UnifiedInvestorNavigation`.
  const { hidden: chatHidden } = useAskAiEntryPoint('public');
  // Read off the organisation the selector has picked, which is the one the Custom companies page
  // renders against; see `UnifiedInvestorNavigation`.
  const { selectedOrganization } = useSelectedOrganization();
  const { hasPermission: canCreateCompanies } = useOrganizationHasCreateCompaniesPermission(selectedOrganization?.id);

  const databaseRows: RailRow[] = [
    ...(showScreener(clientType)
      ? [
          {
            to: SCREEN_COMPANIES_PATH,
            label: 'Screen companies',
            icon: ZoomDocumentIcon,
            ariaCurrent:
              pathname === SCREEN_COMPANIES_PATH || pathname === `${SCREEN_COMPANIES_PATH}/`
                ? ('page' as const)
                : undefined,
          },
          {
            to: SCREEN_FUNDS_PATH,
            label: 'Screen funds',
            icon: SearchDollarIcon,
            ariaCurrent: getAriaCurrent(SCREEN_FUNDS_PATH, pathname),
          },
        ]
      : []),
    ...(showSharedAssets(clientType)
      ? [
          {
            to: MANAGE_ACCESS_PATH,
            label: sharedAssetsLabel(hasSelfServeAccess),
            icon: OfficeWorkerStreamlineCore,
            ariaCurrent: getAriaCurrent(MANAGE_ACCESS_PATH, pathname),
          },
        ]
      : []),
  ];

  return (
    <StyledNavList aria-label="Company navigation" className={railClassName}>
      <TopLevelItemWrap>
        <OrganizationSelectorSidebar />
      </TopLevelItemWrap>
      {!chatHidden && (
        <TopLevelItemWrap>
          <TopLevelRow to="/" label="Home" icon={HomeChimney} current={pathname === '/'} />
        </TopLevelItemWrap>
      )}
      {!chatHidden && <ChatsRailSection />}
      {/*
       * Its own wrapper, so it takes the `lg` gap the wrapper puts between siblings: after the Chats
       * group it is the start of the rail's own destinations, not one more conversation row.
       */}
      <TopLevelItemWrap>
        <TopLevelRow
          to={MY_COMPANY_PATH}
          label="My company"
          icon={BuildingOfficeIcon}
          current={pathname === MY_COMPANY_PATH || (chatHidden && pathname === '/')}
        />
        {canCreateCompanies && (
          <TopLevelRow
            to={CUSTOM_COMPANIES_PATH}
            label="Custom companies"
            icon={ApplicationAdd}
            current={pathname.startsWith(CUSTOM_COMPANIES_PATH)}
          />
        )}
      </TopLevelItemWrap>
      {databaseRows.length > 0 && <RailSection sectionKey="database" title="Database" rows={databaseRows} />}
      <OrganizationRailRow />
    </StyledNavList>
  );
}

/** The company dashboard's own route, which renders *My company* inside this shell. */
const MY_COMPANY_PATH = '/company-dashboard';

/**
 * A top-level row, the investor rail's Home row (`HomeRailRow`) generalised to two destinations: a
 * `NavList.Item` rather than a sub-row, so the selector keeps its specified `lg` above the first row,
 * and an exact current test, because every path contains `/`.
 */
function TopLevelRow(props: {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  current: boolean;
}) {
  const Icon = props.icon;
  return (
    <NavList.Item to={props.to} as={LinkWithSearch} aria-current={props.current ? 'page' : undefined}>
      <NavList.LeadingVisual>
        <Icon width={20} height={20} />
      </NavList.LeadingVisual>
      {props.label}
    </NavList.Item>
  );
}
